// Lib
import { Capacitor } from '@capacitor/core';

// Services
import { intercomBoot, intercomShutdown, intercomShowMessenger } from './intercomService';

// Utils
import getClientConfig from '../utils/getClientConfig';
import { setRollbarUserId, setRollbarApplicationPlatform } from './rollbarService';
import { isCurrentBrowserLogoutAction } from '../auth/authUtils';
import { clearAmplitudeUser } from './amplitudeService';
import { getEnabledExperiments, getUserLocalePreference } from '../../common/users/utils/userPropertyUtils';
import { getTimestamp, TIMES } from '../../common/utils/timeUtil';
import { setNewRelicApplicationVersion, setNewRelicCustomAttribute, setNewRelicUserId } from './newRelicUtils';
import { getPlatformDetailsSelector } from '../platform/platformSelector';
import { isPlatformDesktopPwa, isPlatformElectronMac } from '../platform/utils/platformDetailsUtils';
import { getInitialPlatformDetails } from '../platform/utils/platformInitialisationUtils';
import { getCurrentUserToken } from '../auth/authService';
import { simpleDecodeJwt } from '../auth/authTokenClientUtils';

// Middleware
import analyticsEventMiddleware from './analyticsEventMiddleware';
import amplitudeAnalyticsMiddleware from './amplitudeAnalyticsMiddleware';

// Plugins
import { IntercomPlugin } from '../../capacitor_plugins/intercom';

// Constants
import { INTERCOM_SHOW_HELP_CENTER, INTERCOM_SHOW_MESSAGES } from './analyticsConstants';
import { CURRENT_USER_SET, CURRENT_USER_SET_GUEST } from '../user/currentUserConstants';
import { CAPACITOR_INTERCOM_PLUGIN_NAME } from '../../capacitor_plugins/pluginConstants';
import { HELP_URL } from '../../common/utils/urlConstants';
import { withPayloadLogging } from '../capacitor/capacitorPayloadLogging';

export default () => {
    const config = getClientConfig();

    setNewRelicApplicationVersion(config.appVersion);

    const platformDetails = getInitialPlatformDetails();
    setNewRelicCustomAttribute('applicationType', platformDetails.appType);
    setRollbarApplicationPlatform(platformDetails.applicationPlatform);

    // if no analytics enabled, return empty middleware
    if (!config.analytics || !config.analytics.enabled) {
        return (store) => (next) => (action) => {
            next(action);
        };
    }

    // send user data to intercom
    const sendIntercomUserData = (user, platformDetails) => {
        const createdDate = parseInt(user._id.slice(0, 8), 16);

        const intercomUser = {
            user_id: user._id,
            created_at: createdDate,
            email: user.email,
            name: user.name && user.name.displayName,
            res_width: window.screen.availWidth,
            res_height: window.screen.availHeight,
            hide_default_launcher: true,
            experiments: getEnabledExperiments(user),
            localePreference: getUserLocalePreference(user),
        };

        if (isPlatformElectronMac(platformDetails)) {
            intercomUser['last-seen-on-electron'] = Math.floor(getTimestamp() / TIMES.SECOND);
        }

        if (isPlatformDesktopPwa(platformDetails)) {
            intercomUser['has-used-PWA'] = true;
        }

        if (Capacitor.isPluginAvailable(CAPACITOR_INTERCOM_PLUGIN_NAME)) {
            withPayloadLogging(
                `${CAPACITOR_INTERCOM_PLUGIN_NAME}.initialiseIntercom`,
                (payload) => IntercomPlugin.initialiseIntercom(payload),
                { userId: user._id },
            );
        }

        intercomBoot({
            app_id: config.analytics.appId,
            ...intercomUser,
        });
    };

    const showIntercomMessengerViaCapacitorOrJS = () => {
        if (Capacitor.isPluginAvailable(CAPACITOR_INTERCOM_PLUGIN_NAME)) {
            IntercomPlugin.presentChat();
        } else {
            intercomShowMessenger();
        }
    };

    const showIntercomHelpCenterViaCapacitorOrInNewTab = () => {
        if (Capacitor.isPluginAvailable(CAPACITOR_INTERCOM_PLUGIN_NAME)) {
            IntercomPlugin.presentHelpCenter();
        } else {
            window.open(HELP_URL, '_blank').focus();
        }
    };

    const shutdownIntercom = () => {
        intercomShutdown();
    };

    const getUserIdFromCurrentToken = () => {
        const token = getCurrentUserToken();
        const decodedToken = simpleDecodeJwt(token);

        return decodedToken?._id;
    };

    // return analytics middleware
    return (store) => (next) => {
        const eventMiddleware = analyticsEventMiddleware(store)(next);
        const amplitudeMiddleware = amplitudeAnalyticsMiddleware(store)(next);

        const userIdFromToken = getUserIdFromCurrentToken();
        setRollbarUserId(userIdFromToken);

        return (action) => {
            const updated = next(action);

            eventMiddleware(action);

            // Shut down intercom when user logs out
            if (isCurrentBrowserLogoutAction(action)) {
                clearAmplitudeUser();
                shutdownIntercom();
                return updated;
            }

            if (action.remote) return updated;

            const state = store.getState();
            const platformDetails = getPlatformDetailsSelector(state);

            amplitudeMiddleware(action);

            switch (action.type) {
                case CURRENT_USER_SET:
                    // when we set user data, send it to analytics services
                    sendIntercomUserData(action.user, platformDetails);
                    setRollbarUserId(action.user?._id);
                    setNewRelicUserId(action.user?._id);
                    break;
                case CURRENT_USER_SET_GUEST:
                    // if we only have a guest user, boot interom without user
                    intercomBoot({
                        app_id: config.analytics.appId,
                        hide_default_launcher: true,
                    });
                    setRollbarUserId(null);
                    break;
                case INTERCOM_SHOW_MESSAGES:
                    showIntercomMessengerViaCapacitorOrJS();
                    break;
                case INTERCOM_SHOW_HELP_CENTER:
                    showIntercomHelpCenterViaCapacitorOrInNewTab();
                    break;
                default:
                    break;
            }

            return updated;
        };
    };
};
